/**
 * services/errorMonitoring.ts · 生产错误追踪
 *
 * 替代 Sentry 的轻量方案 (客户端 · 0 依赖):
 *   - window.onerror 全局拦截
 *   - unhandledrejection (Promise reject) 拦截
 *   - 写 audit_events 表 (level=error/warn)
 *   - admin 在 systemHealth 页看错误清单
 *
 * 团队 W9+ 可扩 POST 到 Cloudflare Worker → Sentry / DataDog / 自建 ES
 */

import { getDb, saveDb } from '@core/db';
import { getCurrentUser } from '@core/auth';

interface ErrorEvent {
  level: 'error' | 'warn';
  message: string;
  stack?: string;
  url?: string;
  user_code?: string;
  user_agent?: string;
}

let _enabled = false;

/**
 * 启动错误监控 (main.ts 调用 1 次)
 */
export function initErrorMonitoring(): void {
  if (_enabled) return;
  _enabled = true;

  // 全局错误
  window.addEventListener('error', (e) => {
    captureError({
      level: 'error',
      message: e.message || 'Unknown error',
      stack: e.error?.stack,
      url: e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : location.href,
    });
  });

  // Promise reject
  window.addEventListener('unhandledrejection', (e) => {
    const err = e.reason;
    captureError({
      level: 'error',
      message: err instanceof Error ? err.message : String(err),
      stack: err instanceof Error ? err.stack : undefined,
      url: location.href,
    });
  });

  // eslint-disable-next-line no-console
  console.log('[errorMonitoring] enabled');
}

/**
 * 手动捕获 (业务代码 try/catch 内可调)
 */
export function captureError(evt: Omit<ErrorEvent, 'user_code' | 'user_agent'>): void {
  try {
    const user = getCurrentUser();
    const full: ErrorEvent = {
      ...evt,
      user_code: user?.code || 'anonymous',
      user_agent: navigator.userAgent.substring(0, 200),
    };
    // 写 audit_events 表 (复用 v200.16.1 schema)
    getDb().run(
      `INSERT OR IGNORE INTO audit_events (actor_id, actor_role, module, action, severity, title, body, ip, user_agent, created_at)
       VALUES (?, ?, 'system', ?, ?, ?, ?, ?, ?, datetime('now'))`,
      [
        user?.id || null,
        user?.role || 'anonymous',
        full.level === 'error' ? 'js_error' : 'js_warn',
        full.level,
        full.message.substring(0, 200),
        (full.stack || full.url || '').substring(0, 2000),
        '',
        full.user_agent || '',
      ]
    );
    // 异步保存 (不阻塞 UI)
    setTimeout(() => saveDb().catch(() => { /* noop */ }), 100);
  } catch {
    /* 监控本身不能抛错 */
  }
}

/**
 * 查询近 N 条错误
 */
export function listErrors(limit = 50): {
  id: number; level: string; message: string; actor_role: string;
  user_agent: string; created_at: string;
}[] {
  try {
    const stmt = getDb().prepare(
      `SELECT id, severity AS level, title AS message, actor_role,
              user_agent, created_at
       FROM audit_events
       WHERE module = 'system' AND action IN ('js_error', 'js_warn')
       ORDER BY id DESC LIMIT ?`
    );
    stmt.bind([limit]);
    const rows: any[] = [];
    while (stmt.step()) rows.push(stmt.getAsObject());
    stmt.free();
    return rows;
  } catch {
    return [];
  }
}

/**
 * 错误统计
 */
export function getErrorStats(): { total: number; today: number; weekly: number } {
  try {
    const get = (sql: string): number => {
      const stmt = getDb().prepare(sql);
      stmt.step();
      const v = stmt.getAsObject() as { c: number };
      stmt.free();
      return v.c || 0;
    };
    return {
      total: get(`SELECT COUNT(*) c FROM audit_events WHERE module='system' AND action IN ('js_error','js_warn')`),
      today: get(`SELECT COUNT(*) c FROM audit_events WHERE module='system' AND action IN ('js_error','js_warn') AND strftime('%Y-%m-%d', created_at) = strftime('%Y-%m-%d', 'now')`),
      weekly: get(`SELECT COUNT(*) c FROM audit_events WHERE module='system' AND action IN ('js_error','js_warn') AND created_at >= datetime('now', '-7 days')`),
    };
  } catch {
    return { total: 0, today: 0, weekly: 0 };
  }
}
